import {OrderType, Prescription, SelectOption} from 'types';
import styles from './ProductItem.module.scss';
import {useInlineEdit} from 'hooks/useInlineEdit';
import classNames from 'classnames';
import Checkbox from 'components/Checkbox';
import FeatureGuard from 'components/FeatureGuard';
import useCompanyConfig from 'hooks/useCompanyConfig';
import Select from 'components/Select';
import {
  PRODUCT_PAYMENT_METHOD_OPTIONS,
  PRODUCT_PAYMENT_METHOD_OPTIONS_MAP,
} from 'constants/select-options';
import {useState} from 'react';
import {SingleValue} from 'react-select';
import StatusBadge from 'components/StatusBadge';

interface ProductItemProps {
  disabledToCheck: boolean;
  product: Prescription;
  refetch: () => void;
  selectedSplitItems: number[];
  setSelectedSplitItems: (items: number[]) => void;
  showSplitCheckbox?: boolean;
  orderType?: OrderType;
  onUpdatePaymentMethod?: ({
    drugId,
    payment_method,
  }: {
    drugId: number;
    payment_method: string;
  }) => Promise<any>;
}

function ProductItem({
  disabledToCheck,
  showSplitCheckbox,
  product,
  refetch,
  selectedSplitItems,
  setSelectedSplitItems,
  orderType,
  onUpdatePaymentMethod,
}: ProductItemProps) {
  const [paymentMethod, setPaymentMethod] = useState<SelectOption>(
    PRODUCT_PAYMENT_METHOD_OPTIONS_MAP[product.payment_method]
  );
  const {isPropertyManager, isPickupDropoff, hasFeature} = useCompanyConfig();
  const {priceName, quantityName, totalName, payableAmountDueName} = {
    priceName: `price_${product.id}`,
    quantityName: `quantity_${product.id}`,
    totalName: `total_${product.id}`,
    payableAmountDueName: `payable_amount_due_${product.id}`,
  };
  const productIsNotAvailable =
    hasFeature('hasProductAvailabilityBadge') && product?.not_available;
  const priceEdit = useInlineEdit({
    product,
    fieldName: 'cash_price',
    refetch,
  });

  const quantityEdit = useInlineEdit({
    product,
    fieldName: 'quantity',
    refetch,
  });

  const totalEdit = useInlineEdit({
    product,
    fieldName: 'total_amount',
    refetch,
  });
  const payableCashPriceEdit = useInlineEdit({
    product,
    fieldName: 'payable_cash_price',
    refetch,
  });
  const isSelected = selectedSplitItems.includes(product.id);

  const showCostAndProfit =
    (orderType === 'pickup' && isPickupDropoff) || isPropertyManager;

  const handleChangePaymentMethod = (newValue: SingleValue<SelectOption>) => {
    const selectedPaymentMethod =
      PRODUCT_PAYMENT_METHOD_OPTIONS_MAP[newValue?.value as string];
    setPaymentMethod(selectedPaymentMethod);
    onUpdatePaymentMethod?.({
      drugId: product.id,
      payment_method: selectedPaymentMethod?.value as string,
    });
  };
  return (
    <div className={styles.productList}>
      <div className={styles.productItem}>
        <div className={styles.productInfo}>
          <div className={styles.productNameContainer}>
            <h4 className={styles.productName}>{product.name}</h4>
            {productIsNotAvailable && (
              <StatusBadge color="canceled">Not Available</StatusBadge>
            )}
          </div>
          <div className={styles.productDetails}>
            <label className={styles.productDetail}>
              Unit Price:&nbsp;$
              <input
                name={priceName}
                type="text"
                disabled={productIsNotAvailable}
                value={priceEdit.value}
                onFocus={priceEdit.handleFocus}
                onBlur={priceEdit.handleBlur}
                onKeyDown={priceEdit.handleKeyDown}
                onChange={priceEdit.handleChange}
              />
            </label>
            <label className={styles.productDetail}>
              Quantity:&nbsp;
              <input
                name={quantityName}
                type="text"
                disabled={productIsNotAvailable}
                value={quantityEdit.value}
                onFocus={quantityEdit.handleFocus}
                onBlur={quantityEdit.handleBlur}
                onKeyDown={quantityEdit.handleKeyDown}
                onChange={quantityEdit.handleChange}
              />
            </label>
            <label className={classNames(styles.productDetail)}>
              Total:&nbsp;$
              <input
                name={totalName}
                type="text"
                disabled={productIsNotAvailable}
                value={totalEdit.value}
                onFocus={totalEdit.handleFocus}
                onBlur={totalEdit.handleBlur}
                onKeyDown={totalEdit.handleKeyDown}
                onChange={totalEdit.handleChange}
              />
            </label>
            <FeatureGuard feature="hasCostAndProfitOnOrderDetails">
              {showCostAndProfit && (
                <>
                  <label className={classNames(styles.productDetail)}>
                    Cost:&nbsp;$
                    <input
                      name={payableAmountDueName}
                      type="text"
                      disabled={productIsNotAvailable}
                      value={payableCashPriceEdit.value}
                      onFocus={payableCashPriceEdit.handleFocus}
                      onBlur={payableCashPriceEdit.handleBlur}
                      onKeyDown={payableCashPriceEdit.handleKeyDown}
                      onChange={payableCashPriceEdit.handleChange}
                    />
                  </label>
                  <label className={classNames(styles.productDetail)}>
                    Profit:&nbsp;$
                    <span>
                      {Number(
                        parseFloat((product?.total_amount ?? '0').toString()) -
                          parseFloat(
                            (product?.payable_total_amount ?? '0')!.toString()
                          )
                      )}
                    </span>
                  </label>
                </>
              )}
            </FeatureGuard>
            <div className={styles.paymentMethodSelect}>
              <Select
                isDisabled={productIsNotAvailable}
                placeholder="Payment Method"
                onChange={handleChangePaymentMethod}
                value={paymentMethod}
                isClearable
                options={PRODUCT_PAYMENT_METHOD_OPTIONS}
                size="md"
              />
            </div>
            {showSplitCheckbox && (
              <label className={styles.productDetail}>
                <Checkbox
                  checked={isSelected}
                  onChange={() => {
                    setSelectedSplitItems(
                      isSelected
                        ? selectedSplitItems.filter((id) => id !== product.id)
                        : [...selectedSplitItems, product.id]
                    );
                  }}
                />
              </label>
            )}
          </div>
          {(priceEdit.error || quantityEdit.error || totalEdit.error) && (
            <div className={styles.errorContainer}>
              {priceEdit.error && (
                <div className={styles.error}>{priceEdit.error}</div>
              )}
              {quantityEdit.error && (
                <div className={styles.error}>{quantityEdit.error}</div>
              )}
              {totalEdit.error && (
                <div className={styles.error}>{totalEdit.error}</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProductItem;
